/**
 * The question bank, one list.
 *
 * The bank is the seed questions plus every question that reached the product
 * some other way and was never filed: a question on a paper, one composed
 * inside an assessment, one saved in an assessment session. Those used to live
 * only where they were made, so the library and Add questions never offered
 * them. `ensureInBank` files any such question once, under its course, and
 * `reconcileBank` sweeps every paper at start-up for ones already missing.
 *
 * A question counts as already in the bank when its id, its bank id or its
 * wording matches a row there, so a paper question that came from the bank is
 * never filed twice.
 */

import * as React from "react"

import { assessmentBankQuestionToLibraryItem } from "@/lib/mock/assessment-bank-to-library"
import {
  ASSESSMENT_BANK_QUESTIONS,
  type AssessmentBankQuestion,
} from "@/lib/mock/assessment-add-questions"
import { MOCK_ASSESSMENTS } from "@/lib/mock/exam-data"
import type { LibraryItem } from "@/lib/mock/library"
import { LIBRARY_ITEMS } from "@/lib/mock/library"
import { createPersistedStore, usePersistedStore } from "@/lib/persisted-store"
import { readAssessmentQuestionBankRows } from "@/lib/question-version-history/assessment-session"

export const BANK_ADDITIONS_KEY = "shell:question-bank-additions:v1"

/** Questions filed into the bank after the fact, by id. */
export const bankAdditionsStore = createPersistedStore<Record<string, AssessmentBankQuestion>>(
  BANK_ADDITIONS_KEY,
  () => ({}),
)

export const normaliseStem = (stem: string | undefined) =>
  (stem ?? "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()

type Index = { ids: Set<string>; stems: Set<string> }
let indexCache: { additions: Record<string, AssessmentBankQuestion>; index: Index } | undefined

function bankIndex(): Index {
  const additions = bankAdditionsStore.get()
  if (indexCache && indexCache.additions === additions) return indexCache.index
  const ids = new Set<string>()
  const stems = new Set<string>()
  for (const row of [...ASSESSMENT_BANK_QUESTIONS, ...Object.values(additions)]) {
    ids.add(row.id)
    if (typeof row.questionId === "string") ids.add(row.questionId)
    const stem = normaliseStem(row.stem)
    if (stem) stems.add(stem)
  }
  for (const item of LIBRARY_ITEMS) {
    ids.add(item.id)
    ids.add(item.questionId)
  }
  indexCache = { additions, index: { ids, stems } }
  return indexCache.index
}

export function isInBank(row: { id: string; questionId?: string; stem?: string }): boolean {
  const { ids, stems } = bankIndex()
  if (ids.has(row.id)) return true
  if (row.questionId && ids.has(row.questionId)) return true
  const stem = normaliseStem(row.stem)
  return stem.length > 0 && stems.has(stem)
}

/** The bank folder for a course code ("NURS-210"), or none. */
export function courseFolderIdForCode(
  code: string | undefined,
  folders: ReadonlyArray<{ id: string; name: string; parentId: string | null }>,
): string {
  if (!code) return ""
  const wanted = code.trim().toLowerCase()
  const match = folders.find(
    (folder) => folder.parentId === null && folder.name.trim().toLowerCase().startsWith(wanted),
  )
  return match?.id ?? ""
}

export type PaperQuestionLike = {
  id: string
  questionId?: string
  stem: string
  title?: string
  type: string
  topic?: string
  difficulty?: string
  blooms?: string
  points?: number
  pointBiserial?: number
  usedBefore?: boolean
  usedCount?: number
  usedInSameCourse?: boolean
  status?: "Draft" | "Published"
  author?: string
  authorEmail?: string
}

/** A question from a paper, as a bank row. */
export function bankRowFromPaperQuestion(
  row: PaperQuestionLike,
  ctx: { folderId?: string; sourceLabel?: string } = {},
): AssessmentBankQuestion {
  const difficulty = row.difficulty === "Easy" || row.difficulty === "Hard" ? row.difficulty : "Medium"
  return {
    id: row.id,
    title: row.title?.trim() || row.stem,
    stem: row.stem,
    type: row.type,
    topic: row.topic || "Untagged",
    difficulty,
    difficultyTagged: row.difficulty === "Easy" || row.difficulty === "Medium" || row.difficulty === "Hard",
    sourceKind: "library",
    sourceLabel: ctx.sourceLabel ?? "Question bank",
    folderId: ctx.folderId ?? "",
    pbi: row.usedBefore ? String(row.pointBiserial ?? "--") : "--",
    points: row.points,
    author: row.author,
    authorEmail: row.authorEmail,
    timesUsed: row.usedCount,
    usedInSameCourse: row.usedInSameCourse,
    neverUsed: !row.usedBefore,
    status: row.status === "Draft" ? "Draft" : "Published",
  } as AssessmentBankQuestion
}

/**
 * File every question that is not in the bank yet. Returns the ones added.
 * Safe to call again: a question already there is skipped.
 */
export function ensureInBank(
  rows: ReadonlyArray<AssessmentBankQuestion | (PaperQuestionLike & { sourceKind?: undefined })>,
  ctx: { folderId?: string } = {},
): AssessmentBankQuestion[] {
  const added: AssessmentBankQuestion[] = []
  const seen = new Set<string>()
  for (const row of rows) {
    const stem = normaliseStem(row.stem)
    if (!row.id || !stem || seen.has(stem) || isInBank(row as { id: string; stem: string })) continue
    seen.add(stem)
    const isBankShape = "sourceKind" in row && row.sourceKind != null
    added.push(
      isBankShape
        ? ({ ...(row as AssessmentBankQuestion), folderId: (row as AssessmentBankQuestion).folderId || ctx.folderId || "" })
        : bankRowFromPaperQuestion(row as PaperQuestionLike, { folderId: ctx.folderId }),
    )
  }
  if (added.length > 0) {
    bankAdditionsStore.set((current) => ({
      ...current,
      ...Object.fromEntries(added.map((row) => [row.id, row])),
    }))
  }
  return added
}

/**
 * Sweep every paper and assessment session for questions the bank is missing.
 * Run once at start-up, so a question made anywhere is in the bank by the time
 * anyone looks for it.
 */
export function reconcileBank(
  folders: ReadonlyArray<{ id: string; name: string; parentId: string | null }>,
  /** What is on each paper. Passed in so the bank does not depend on the paper. */
  papersFor: (assessmentId: string) => ReadonlyArray<PaperQuestionLike>,
): number {
  let total = 0
  for (const assessment of MOCK_ASSESSMENTS) {
    const folderId = courseFolderIdForCode(assessment.course, folders)
    const paper = papersFor(assessment.id)
    total += ensureInBank(paper, { folderId }).length
    const session = [...readAssessmentQuestionBankRows(assessment.id).values()]
    total += ensureInBank(session, { folderId }).length
  }
  return total
}

let byIdCache: { additions: Record<string, AssessmentBankQuestion>; map: Map<string, AssessmentBankQuestion> } | undefined

/** One bank row by id, whether seeded or filed. */
export function bankRowById(id: string): AssessmentBankQuestion | undefined {
  const additions = bankAdditionsStore.get()
  if (!byIdCache || byIdCache.additions !== additions) {
    const map = new Map<string, AssessmentBankQuestion>()
    for (const row of ASSESSMENT_BANK_QUESTIONS) map.set(row.id, row)
    for (const row of Object.values(additions)) map.set(row.id, row)
    byIdCache = { additions, map }
  }
  return byIdCache.map.get(id)
}

/** Every bank row: the seed bank and what was filed into it. */
export function bankRows(): AssessmentBankQuestion[] {
  return [...ASSESSMENT_BANK_QUESTIONS, ...Object.values(bankAdditionsStore.get())]
}

/** The bank as library rows. Filed questions follow the seed rows. */
export function bankLibraryItems(): LibraryItem[] {
  const filed = Object.values(bankAdditionsStore.get())
  return [
    ...LIBRARY_ITEMS,
    ...filed.map((row, index) =>
      assessmentBankQuestionToLibraryItem(row, ASSESSMENT_BANK_QUESTIONS.length + index),
    ),
  ]
}

export function useQuestionBankRows(): AssessmentBankQuestion[] {
  const [additions] = usePersistedStore(bankAdditionsStore)
  return React.useMemo(
    () => [...ASSESSMENT_BANK_QUESTIONS, ...Object.values(additions)],
    [additions],
  )
}
